import React from 'react';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArticleIcon from '@mui/icons-material/Article';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import BusinessIcon from '@mui/icons-material/Business';
import ShieldIcon from '@mui/icons-material/Shield';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { BRAND_COLORS } from '../constants/colors';

// Define fixed menu width
const MENU_WIDTH = 240;

const CustomMenuItem = ({ 
  to, 
  icon, 
  text, 
  isActive 
}: { 
  to: string; 
  icon: React.ReactNode; 
  text: string; 
  isActive: boolean; 
}) => {
  const navigate = useNavigate();

  return (
    <ListItemButton
      onClick={() => navigate(to)}
      sx={{
        borderLeft: '5px solid transparent',
        padding: '10px 16px',
        color: '#555',
        borderRadius: 0,
        minHeight: '48px',
        width: '100%',
        '&:hover': {
          borderLeft: '5px solid #DDD8D6',
          color: '#1C5195',
          backgroundColor: 'rgba(221, 216, 214, 0.1)',
        },
        ...(isActive && {
          borderLeft: '5px solid #1C5195',
          backgroundColor: 'rgba(28, 81, 149, 0.05)',
          color: '#1C5195',
          fontWeight: 600,
        }),
        transition: 'all 0.2s ease',
      }}
    >
      <ListItemIcon
        sx={{
          color: 'inherit',
          minWidth: '40px',
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={text}
        sx={{
          '& .MuiListItemText-primary': {
            color: 'inherit',
            fontWeight: 'inherit',
          },
        }}
      />
    </ListItemButton>
  );
};

export const CustomMenu = (props: any) => {
  const location = useLocation();

  // Get current user to check if they have a driver profile or company role
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const hasDriverProfile = user.driver !== undefined && user.driver !== null;
  const isCompanyUser = user.userRole === 'company';
  const isSuperAdmin = user.isSuperAdmin === true;
  const isAdminOrOperator = Array.isArray(user.roles) && (user.roles.includes('admin') || user.roles.includes('operator'));
  // Anyone who isn't company/superAdmin/admin/operator is redirected to
  // /completed-forms by CustomLayout for every other route — mirror that here
  // so the nav doesn't flash a full menu of pages they're about to be bounced
  // away from.
  const isRestrictedUser = !isCompanyUser && !isSuperAdmin && !isAdminOrOperator;

  const menuItems = isRestrictedUser ? [
    {
      to: '/completed-forms',
      icon: <AssignmentIcon />,
      text: 'Completed Forms',
      path: '/completed-forms'
    },
  ] : [
    {
      to: '/',
      icon: <DashboardIcon sx={{ color: BRAND_COLORS.PRIMARY }} />,
      text: 'Dashboard',
      path: '/'
    },
    {
      to: '/equipment',
      icon: <LocalShippingIcon />,
      text: 'Vehicles',
      path: '/equipment'
    },
    {
      to: '/insurance',
      icon: <ShieldIcon />,
      text: 'Insurance',
      path: '/insurance'
    },
    {
      to: '/users',
      icon: <PeopleIcon />,
      text: 'Users',
      path: '/users'
    },
    // Add My Driver Profile menu item for users with driver profiles
    ...(hasDriverProfile ? [{
      to: `/users/${user.id}/show/1`,
      icon: <PersonAddIcon />,
      text: 'My Driver Profile',
      path: `/users/${user.id}/show/1`
    }] : []),
    {
      to: '/driver-time-logs',
      icon: <AccessTimeIcon />,
      text: 'Driver Time Logs',
      path: '/driver-time-logs'
    },
    {
      to: '/completed-forms',
      icon: <AssignmentIcon />,
      text: 'Completed Forms',
      path: '/completed-forms'
    },
    {
      to: '/reminders',
      icon: <NotificationsIcon />,
      text: 'Reminders',
      path: '/reminders'
    },
    {
      to: '/services',
      icon: <BusinessCenterIcon />,
      text: 'Services',
      path: '/services'
    },
    {
      to: '/settings/form-emails',
      icon: <SettingsIcon />,
      text: 'Form Settings',
      path: '/settings/form-emails'
    },
    ...(isCompanyUser || isAdminOrOperator ? [{
      to: '/company/profile',
      icon: <BusinessIcon />,
      text: 'Company Profile',
      path: '/company/profile'
    }] : []),
    ...(isSuperAdmin ? [{
      to: '/tenants',
      icon: <AdminPanelSettingsIcon />,
      text: 'Tenants',
      path: '/tenants'
    }] : [])
  ];

  return (
    <Box 
      className="dv-side-menu"
      sx={{
        textTransform: 'capitalize',
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#fff',
        margin: '16px 0 16px 16px',
        borderRadius: '16px',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1200,
        height: 'calc(100vh - 32px)',
        display: 'flex',
        flexDirection: 'column',
        width: MENU_WIDTH,
      }}
    >
      <Box 
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '32px 16px 16px 16px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          marginBottom: '16px',
        }}
      >
        <Box
          component="img"
          src="/logo-dv-full-text.png"
          alt="DV Logo"
          sx={{ 
            height: 32,
            maxWidth: '80%',
            objectFit: 'contain',
          }}
        />
      </Box>
      
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        <List disablePadding>
          {menuItems.map((item) => (
            <CustomMenuItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              text={item.text}
              isActive={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
            />
          ))}
        </List>
      </Box>
    </Box>
  );
};